import Box from "@mui/material/Box";
import React, { useContext, useState, useMemo, useCallback, useEffect } from "react";
import Header from "../../../components/global/Header.jsx";
import {
  GRID_CHECKBOX_SELECTION_COL_DEF,
  useGridApiRef,
  GridLogicOperator,
} from "@mui/x-data-grid-pro";
import { UserContext } from "../../../UserContext.js";
import { boardStyles } from "../../../styles/InLineStyles/BoardStyles.js";
import CheckboxRow from "../RowDelete/CheckboxRow.jsx";
import ModalRowDelete from "../RowDelete/ModalRowDelete.jsx";
import { useLocation } from "react-router-dom";
import { getTableStyles } from "../../../styles/InLineStyles/BoardStyles.js";

// Importando componentes y hooks modularizados
import BoardDataGrid from "./components/BoardDataGrid.jsx";
import BoardGroup from "./components/BoardGroup.jsx";
import useBoard from "./hooks/useBoard.jsx";
import { cleanHtmlTags } from "./utils/boardUtils.js";

const BaseBoard = ({
  columns,
  groups,
  getGroupRows,
  setGroupItems,
  apiUrl,
  controlEdit,
  refreshState,
  updateBoard,
  updateRows,
  viewFilter,
  setViewFilter,
  columnsFilter,
  setColumnsFilter,
  pinnedColumns,
  setPinnedColumns,
  setSelectionRow,
  selectionRow,
  selectedView,
  checkboxSelection,
  height,
  maxHeight,
  isLoadingRows,
}) => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const pathname = location.pathname;
  const updateValue = searchParams.get("update");
  const [updateParams] = useState(updateValue);

  const apiRef = useGridApiRef();
  const { key, setKey } = useContext(UserContext);
  const isMobile = window.innerWidth <= 600;
  const { boardState, setBoardState } = useBoard({
    key,
    apiUrl,
    pathname,
    setGroupItems,
    groups,
    updateRows,
    updateParams,
  });
  
  useEffect(() => {
    setKey(localStorage?.getItem("jwt"));
  }, [setKey]);

  const initialPinnedColumns = useMemo(
    () => ({
      left: [
        GRID_CHECKBOX_SELECTION_COL_DEF.field,
        ...columns.map((column) => column.field).slice(0, 3),
      ],
    }),
    [columns]
  );

  const handleSelectionModelChange = useCallback(
    (selectionModel) => {
      setSelectionRow(selectionModel);
      setBoardState((prev) => ({
        ...prev,
        isModalOpen: selectionModel?.length > 0,
      }));
    },
    [setSelectionRow, setBoardState]
  );

  const handleFilterChange = useCallback(
    (newFilterModel) => {
      if (newFilterModel?.quickFilterValues?.length > 0) {
        setBoardState((prev) => ({
          ...prev,
          expandedGroups: new Set(groups.map((_, i) => i)),
        }));
      }
      setViewFilter(newFilterModel);
    },
    [groups, setViewFilter, setBoardState]
  );

  const toggleGroup = useCallback(
    (index) => {
      setBoardState((prev) => {
        const newExpanded = new Set(prev.expandedGroups);
        if (newExpanded.has(index)) {
          newExpanded.delete(index);
        } else {
          newExpanded.add(index);
        }
        return { ...prev, expandedGroups: newExpanded };
      });
    },
    [setBoardState]
  );

  return (
    <div style={{ padding: isMobile ? "5px 10px" : "0px" }}>
      <Header title="" />

      {groups.map((groupName, index) => (
        <BoardGroup
          key={index}
          groupName={groupName}
          index={index}
          isExpanded={boardState.expandedGroups.has(index)}
          onToggle={() => toggleGroup(index)}
          isMobile={isMobile}
        >
          <CheckboxRow
            isModalOpen={boardState.isModalOpen}
            setIsModalOpen={(isOpen) =>
              setBoardState((prev) => ({ ...prev, isModalOpen: isOpen }))
            }
            selectionRow={selectionRow}
            setDeleteInput={(input) =>
              setBoardState((prev) => ({ ...prev, deleteInput: input }))
            }
            setIsDeleteModalOpen={(isOpen) =>
              setBoardState((prev) => ({ ...prev, isDeleteModalOpen: isOpen }))
            }
            isDeleteModalOPen={boardState.isDeleteModalOpen}
          />

          <ModalRowDelete
            setIsDeleteModalOpen={(isOpen) =>
              setBoardState((prev) => ({ ...prev, isDeleteModalOpen: isOpen }))
            }
            isModalOpen={boardState.isModalOpen}
            isDeleteModalOpen={boardState.isDeleteModalOpen}
            setIsModalOpen={(isOpen) =>
              setBoardState((prev) => ({ ...prev, isModalOpen: isOpen }))
            }
            setDeleteInput={(input) =>
              setBoardState((prev) => ({ ...prev, deleteInput: input }))
            }
            deleteInput={boardState.deleteInput}
            selectionRow={selectionRow}
            pathname={pathname}
            updateBoard={updateBoard}
          />

          <Box
            m="0px 0 0 0"
            height={height}
            maxHeight={maxHeight}
            width="100%"
            bgcolor="#fefcfd"
            overflow="hidden"
            sx={boardStyles}
            style={{
              borderLeft: getTableStyles(groupName).border,
            }}
          >
            <BoardDataGrid
              rows={getGroupRows(groupName)}
              columns={columns}
              apiRef={apiRef}
              filterModel={
                updateParams
                  ? {
                      items: [
                        {
                          id: 1,
                          field: "id",
                          operator: "equals",
                          value: updateParams,
                        },
                      ],
                      quickFilterLogicOperator: GridLogicOperator.Or,
                      quickFilterExcludeHiddenColumns: true,
                    }
                  : viewFilter
              }
              onFilterModelChange={handleFilterChange}
              columnVisibilityModel={columnsFilter}
              onColumnVisibilityModelChange={setColumnsFilter}
              pinnedColumns={pinnedColumns}
              onPinnedColumnsChange={setPinnedColumns}
              onCellEditStop={controlEdit}
              checkboxSelection={checkboxSelection}
              initialState={initialPinnedColumns}
              onRowSelectionModelChange={handleSelectionModelChange}
              onClipboardCopy={cleanHtmlTags}
              isLoadingRows={isLoadingRows}
              pathname={pathname}
              updateBoard={updateBoard}
              selectedView={selectedView}
            />
          </Box>
        </BoardGroup>
      ))}
    </div>
  );
};

export default BaseBoard;
